import type { ISolution } from '@eureka/shared/solution-card/src/types';
import type { REPORT_TEMPLATE } from '@/types';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface ModuleReference {
    moduleType: REPORT_TEMPLATE;
    taskGroupType: string;
    references: ISolution[];
}

export const ReferenceModelService = Symbol('ReferenceModelService');

export interface IReferenceModelService {
    // 内部使用：注册引用数据
    registerModuleReferences(moduleType: REPORT_TEMPLATE, taskGroupType: string, references: ISolution[]): void;

    // 外部使用：根据DOM元素获取引用数据
    getReferenceFromElement(element: Element, index: number): ISolution | undefined;

    // 外部使用：根据DOM元素获取模块信息
    getTaskGroupTypeFromElement(element: Element): string;

    // 外部使用：根据模块信息获取所有引用（用于来源抽屉）
    getModuleReferences(moduleType: REPORT_TEMPLATE, taskGroupType: string): ISolution[];
}

@injectable()
export class ReferenceModelServiceImpl implements IReferenceModelService {
    private moduleReferences = <Map<string, ModuleReference>>(new Map());

    /**
     * 注册模块的引用数据
     * @param moduleType 模块类型
     * @param taskGroupType 任务组类型
     * @param references 引用数据列表
     */
    registerModuleReferences(
        moduleType: REPORT_TEMPLATE,
        taskGroupType: string,
        references: ISolution[]
    ) {
        const key = this.getModuleKey(moduleType, taskGroupType);
        this.moduleReferences.set(key, {
            moduleType,
            taskGroupType,
            references,
        });
        console.log(`Registered references for ${key}:`, references.length);
    }

    /**
     * 获取模块的引用数据
     * @param moduleType 模块类型
     * @param taskGroupType 任务组类型
     * @returns 引用数据列表
     */
    getModuleReferences(moduleType: REPORT_TEMPLATE, taskGroupType: string): ISolution[] {
        const key = this.getModuleKey(moduleType, taskGroupType);
        const moduleRef = this.moduleReferences.get(key);
        return moduleRef?.references || [];
    }

    /**
     * 根据DOM元素获取引用数据
     * @param element 目标元素
     * @param index 引用索引（1-based）
     * @returns 引用数据
     */
    getReferenceFromElement(element: Element, index: number): ISolution | undefined {
        const moduleInfo = this.findModuleFromElement(element);
        if (!moduleInfo) return undefined;

        const references = this.getModuleReferences(moduleInfo.moduleType, moduleInfo.taskGroupType);
        return references[index - 1]; // Convert to 0-based index
    }

    /**
     * 根据DOM元素获取模块信息
     * @param element 目标元素
     * @returns 模块信息
     */
    getTaskGroupTypeFromElement(element: Element): string {
        const moduleInfo = this.findModuleFromElement(element);
        return moduleInfo?.taskGroupType || ''
    }

    /**
     * 根据DOM元素查找对应的模块信息
     * @param element 目标元素
     * @returns 模块信息
     */
    private findModuleFromElement(element: Element): { moduleType: REPORT_TEMPLATE; taskGroupType: string } | null {
        // 查找最近的模块容器
        const moduleContainer = element.closest('[class*="report-card-template__"], [class*="report-card-task__"]');
        if (!moduleContainer) return null;

        const classList = Array.from(moduleContainer.classList);

        // 提取模块类型
        const templateClass = classList.find(cls => cls.startsWith('report-card-template__'));
        if (!templateClass) return null;

        const moduleType = templateClass.replace('report-card-template__', '') as REPORT_TEMPLATE;

        // 提取任务组类型
        const taskClass = classList.find(cls => cls.startsWith('report-card-task__'));
        if (!taskClass) return null;

        const taskGroupType = taskClass.replace('report-card-task__', '');

        console.log('Found module:', { moduleType, taskGroupType });
        return { moduleType, taskGroupType };
    }

    private getModuleKey(moduleType: REPORT_TEMPLATE, taskGroupType: string): string {
        return `${moduleType}__${taskGroupType}`;
    }
}
